import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * Sub-levels are lazy-loaded from a JSON blob embedded in the page
 * (rendered by header-drawer.liquid as <script type="application/json">).
 * The JSON element ID is passed via data-menu-data attribute on <header-drawer>.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  loadSubmenu(event) {
    const summary = event.target.closest('summary');
    if (!summary) return;

    const details = summary.closest('details');
    if (!details) return;

    const submenuList = details.querySelector('[data-submenu-handle][data-loaded="false"]');
    if (!submenuList) return;

    const menuDataEl = document.getElementById(this.dataset.menuData);
    if (!menuDataEl) return;

    let allItems;
    try {
      allItems = JSON.parse(menuDataEl.textContent);
    } catch {
      console.error('[header-drawer] Could not parse menu data JSON. Check the <script type="application/json"> tag.');
      return;
    }

    submenuList.dataset.loaded = 'true';

    const parentHandle = submenuList.dataset.parentHandle;
    const childHandle = submenuList.dataset.childHandle;

    let children;

    if (parentHandle && childHandle) {
      const parentItem = allItems.find(item => item.handle === parentHandle);
      const childItem = parentItem?.children?.find(c => c.handle === childHandle);
      children = childItem?.children ?? [];
    } else {
      const handle = submenuList.dataset.submenuHandle;
      const item = allItems.find(i => i.handle === handle);
      children = item?.children ?? [];
    }

    if (!children.length) return;

    const fragment = document.createDocumentFragment();

    children.forEach((child, index) => {
      const li = document.createElement('li');
      li.className = 'menu-drawer__list-item';
      li.style.setProperty('--menu-drawer-animation-index', String(index + 1));

      const a = document.createElement('a');
      a.href = child.url;
      a.className = `menu-drawer__menu-item menu-drawer__menu-item--child focus-inset${
        child.current ? ' menu-drawer__menu-item--active' : ''
      }`;
      if (child.current) a.setAttribute('aria-current', 'page');

      const handle = submenuList.dataset.submenuHandle;
      a.id = `HeaderDrawer-${handle}-${child.handle}`;

      const span = document.createElement('span');
      span.className = 'menu-drawer__menu-item-text wrap-text';
      span.textContent = child.title;

      a.appendChild(span);
      li.appendChild(a);
      fragment.appendChild(li);
    });

    submenuList.appendChild(fragment);
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}